<template>
  <div>
    <p
      v-if="!loading && definitions.length"
      class="caption blue-grey--text text--darken-3 mb-3"
      data-testid="routine-definitions-label-totalItems"
    >
      {{
        $t('common.resultWithCount', {
          count: totalRecords,
        })
      }}
    </p>

    <v-card outlined v-show="showTable">
      <v-data-table
        class="routine-definitions-table"
        :loading="loading"
        calculate-widths
        data-private
        disable-sort
        :headers="headers"
        :items="definitionsData"
        :server-items-length="totalRecords"
        :page="currentPage"
        :footer-props="footerOptions"
        @click:row="handleRowClick"
        @update:options="$emit('handleOptionsChange', $event)"
      >
        <template v-slot:[`item.name`]="{ item }">
          <div class="d-flex align-center">
            <div class="active-container">
              <div
                class="rounded-circle me-3"
                style="width: 8px !important; height: 8px !important"
                :class="item.isActive ? 'green' : 'yellow'"
              />
              <v-icon
                v-if="item.isGloballyAssigned"
                size="16"
                color="blue-grey"
                class="ma-0 pa-0"
                :style="{ opacity: item.isActive ? 1 : 0.6 }"
              >
                mdi-earth
              </v-icon>
            </div>
            <v-tooltip top max-width="300px">
              <template v-slot:activator="{ on }">
                <span
                  v-on="on"
                  v-snip="1"
                  class="body-2 link-td"
                  :style="{ opacity: item.isActive ? 1 : 0.6 }"
                  >{{ item.name }}</span
                >
              </template>
              {{ item.name }}
            </v-tooltip>
          </div>
        </template>

        <template v-slot:[`item.typeOrFrequency`]="{ item }">
          <div
            v-if="item.frequency || item.type"
            class="d-flex mt-2 align-center"
            :style="{ opacity: item.isActive ? 1 : 0.6 }"
          >
            <v-chip small outlined class="me-2" v-if="item.frequency">
              {{ $t(`routines.frequency.${item.frequency}`) }}
            </v-chip>
            <v-chip small outlined v-if="item.type">
              {{ $t(`routines.type.${item.type}`) }}
            </v-chip>
          </div>
          <div
            v-else
            class="grey--text text--darken-1"
            :style="{ opacity: item.isActive ? 1 : 0.6 }"
          >
            {{ '-' }}
          </div>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex align-center justify-end">
            <div class="d-flex mr-8">
              <v-tooltip top max-width="241" v-if="!canStartRoutine(item)">
                <template v-slot:activator="{ on }">
                  <v-icon
                    v-on="on"
                    class="me-2"
                    color="blue-grey darken-1"
                    size="16"
                    :style="{ opacity: item.isActive ? 1 : 0.6 }"
                  >
                    mdi-help-circle-outline
                  </v-icon>
                </template>
                {{ $t('routines.start_routine_helper') }}
              </v-tooltip>
              <v-btn
                small
                color="blue"
                rounded
                id="start-routine-button"
                data-tourid="routineDefinition-card-startRoutine-button"
                :disabled="!canStartRoutine(item)"
                @click.stop.prevent="$emit('startRoutine', item, 'table')"
              >
                <v-icon size="16" class="ms-n1 me-1 white--text" color="white"
                  >mdi-play</v-icon
                >
                <p class="white--text ma-0 pa-0">
                  {{ $t('common.button.start') }}
                </p>
              </v-btn>
            </div>

            <ActionsWrapper
              source="table"
              :definition="item"
              :organizationId="selectedOrganizationId"
              @getRoutines="$emit('getRoutines')"
            />
          </div>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import mixin from '@/mixins/mixin'
import routineDefinitionPermissions from '@/mixins/routineDefinitionPermissions.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin'

import ActionsWrapper from './ActionsWrapper.vue'

export default {
  name: 'RoutineDefinitionsTableView',

  mixins: [mixin, trackerMixin, routineDefinitionPermissions],

  props: {
    definitions: {
      type: Array,
      required: true,
    },
    totalRecords: {
      type: Number,
      required: true,
    },
    selectedOrganizationId: {
      type: String,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    currentPage: {
      type: Number,
      required: true,
    },
  },

  components: {
    ActionsWrapper,
  },

  data() {
    return {
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
      headers: [
        {
          text: this.$t('common.name'),
          value: 'name',
          width: 300,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
        {
          text: this.$t('routines.type_frequency'),
          value: 'typeOrFrequency',
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
          width: 200,
        },
        {
          text: _.capitalize(this.$t('common.action_other')),
          align: 'end',
          value: 'actions',
        },
      ],
    }
  },

  computed: {
    ...mapGetters({
      account: 'account',
    }),

    definitionsData() {
      return this.definitions.map(definition => definition.definition)
    },

    rows() {
      return this.definitions.map(definition => ({
        ...definition.definition,
        canStartRoutine: this.canStartRoutine(definition),
      }))
    },

    showTable() {
      return this.loading || this.definitions.length > 0
    },
  },

  methods: {
    handleRowClick(item) {
      this.account.isAdmin && this.selectedOrganizationId
        ? this.navigateTo({
            name: 'reverAdminRoutineDefinitionView',
            params: {
              organizationId: this.selectedOrganizationId,
              routineId: item.id,
            },
          })
        : this.navigateTo({
            name: 'routineDefinitionView',
            params: {
              routineId: item.id,
            },
          })

      this.trackRoutineViewNavigate({
        action: 'goToRoutineDefinition',
        source: 'table',
        id: item.id,
      })
    },

    canStartRoutine(definition) {
      return (
        definition.isActive &&
        this.getRoutineDefinitionPermissions({
          routineDefinition: definition,
        }).start
      )
    },
  },
}
</script>
<style lang="scss">
.active-container {
  display: flex;
  align-items: center;
  width: 46px;
}
</style>
